import { getWidgetMeta, WIDGET_TYPES } from './widgetRegistry';
import {
  getVcWidgetMeta,
  listVcWidgetTypes,
  VC_WIDGET_TYPES,
} from '../vectorCapital/vectorCapitalWidgetRegistry';
import { getMyWidgetMeta, isMyWidgetType, MY_WIDGET_TYPES } from './myData/myWidgetRegistry';
import { getMarketWidgetMeta, isMarketWidgetType, MARKET_WIDGET_TYPES } from '../market/marketWidgetRegistry';

export const OVERVIEW_WIDGET_GROUP_ID = 'overview';
export const VC_WIDGET_GROUP_ID = 'vc';
export const OVERVIEW_WIDGET_GROUP_LABEL = 'Overview widgets';
export const VC_WIDGET_GROUP_LABEL = 'Strategy dashboard widgets';
export const MY_WIDGET_GROUP_ID = 'mine';
export const MY_WIDGET_GROUP_LABEL = 'My accounts';
export const TRADE_WATCHER_WIDGET_GROUP_ID = 'tradeWatcher';
export const TRADE_WATCHER_WIDGET_GROUP_LABEL = 'Trade Watcher (NinjaTrader)';
export const MARKET_WIDGET_GROUP_ID = 'market';
export const MARKET_WIDGET_GROUP_LABEL = 'Market (TradingView)';

export { isMyWidgetType, isMarketWidgetType };

export function isVcWidgetType(type) {
  return Boolean(type && VC_WIDGET_TYPES[type]);
}

/** Resolve metadata for Overview KPI widgets and Vector Capital section widgets. */
export function getDashboardWidgetMeta(type) {
  if (isVcWidgetType(type)) return getVcWidgetMeta(type);
  if (isMyWidgetType(type)) return getMyWidgetMeta(type);
  if (isMarketWidgetType(type)) return getMarketWidgetMeta(type);
  return getWidgetMeta(type);
}

export function dashboardHasVcWidgets(dashboard) {
  const widgets = dashboard?.widgets;
  if (!Array.isArray(widgets) || widgets.length === 0) return false;
  return widgets.some((w) => isVcWidgetType(w?.type));
}

/**
 * All widget types grouped for the add-widget picker. My accounts first (the
 * user's own data), then Market. personalOnly (no company): My accounts (minus
 * those that need company strategies) and Market.
 */
export function listAllDashboardWidgetTypeGroups({ personalOnly = false, companyDashboard = false } = {}) {
  const mine = {
    id: MY_WIDGET_GROUP_ID,
    label: MY_WIDGET_GROUP_LABEL,
    items: MY_WIDGET_TYPES
      .filter((item) => item.group !== TRADE_WATCHER_WIDGET_GROUP_ID)
      .filter((item) => !(personalOnly && item.needsCompanyStrategies))
      .map((item) => ({ ...item, group: MY_WIDGET_GROUP_ID })),
  };
  const tradeWatcher = {
    id: TRADE_WATCHER_WIDGET_GROUP_ID,
    label: TRADE_WATCHER_WIDGET_GROUP_LABEL,
    items: MY_WIDGET_TYPES.filter((item) => item.group === TRADE_WATCHER_WIDGET_GROUP_ID),
  };
  // Market widgets show the same thing to everyone, so they fit any dashboard.
  const market = {
    id: MARKET_WIDGET_GROUP_ID,
    label: MARKET_WIDGET_GROUP_LABEL,
    items: MARKET_WIDGET_TYPES.map((item) => ({ ...item, group: MARKET_WIDGET_GROUP_ID })),
  };
  if (personalOnly) return [mine, tradeWatcher, market];
  // The company dashboard is shared by every member: no per-person widgets on it.
  return [
    ...(companyDashboard ? [] : [mine, tradeWatcher]),
    market,
    {
      id: OVERVIEW_WIDGET_GROUP_ID,
      label: OVERVIEW_WIDGET_GROUP_LABEL,
      items: WIDGET_TYPES.map((item) => ({ ...item, group: OVERVIEW_WIDGET_GROUP_ID })),
    },
    {
      id: VC_WIDGET_GROUP_ID,
      label: VC_WIDGET_GROUP_LABEL,
      items: listVcWidgetTypes().map((item) => ({ ...item, group: VC_WIDGET_GROUP_ID })),
    },
  ];
}

export function listAllDashboardWidgetTypes() {
  return listAllDashboardWidgetTypeGroups().flatMap((group) => group.items);
}

/** Widget picker list — same options on Overview and Overview Test. */
export function widgetTypesForDashboard(_dashboard) {
  return listAllDashboardWidgetTypes();
}

export function widgetTypeGroupsForDashboard(_dashboard, { personalOnly = false, companyDashboard = false } = {}) {
  return listAllDashboardWidgetTypeGroups({ personalOnly, companyDashboard });
}
